import { ViewEncapsulation, Component, OnInit, Input, HostBinding } from '@angular/core';
import moment from 'moment';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MatRippleModule } from '@angular/material/core';

import { Offer, OfferHeader } from '../../../models/offer';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { CURRENCY_VALUES_SYMBOLE } from '../../../models/list-cst';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { getFormatDate } from '../../../../shared/utils/resolved-format-date';

import { createOfferPeriod } from './create-offer-period';

interface DisplayedOfferHeader {
	rfqRef: string;
	referenceAm: string;
	soldTo: string;
	shipTo: string;
	endUser: string;
	period: string;
	idd: string;
	tmode: string;
	incoterm: string;
	priceUnit: string;
	currency: string;
	comment?: string | undefined;
	lastModificationDate: string;
	validityDate: string;
	manager: string;
	assistant: string;
	technician: string;
	weightAndInvoicing: string;
	paymentTerms: string;
	incotDescription: string;
}
interface ProductsFamily {
	productFamilyCategory: string;
	form: string;
	familyCode: string;
	noOfProducts: number;
	quantity?: number;
}

@Component({
	selector: 'copo-offer-offer-header-card-block',
	templateUrl: './offer-header-card-block.component.html',
	styleUrl: './offer-header-card-block.component.css',
	encapsulation: ViewEncapsulation.Emulated,

	imports: [MatRippleModule, TranslateModule, MatTooltipModule, NgIf, NgFor, MatIconModule]
})
export class OfferHeaderCardBlockComponent implements OnInit {
	@HostBinding('class.passthrough') readonly passthrough = true;

	@Input() header!: OfferHeader;
	@Input() bocs!: BocClient[];
	@Input() offers!: Offer;
	displayedOfferHeader!: DisplayedOfferHeader;
	productsFamilies?: ProductsFamily[];
	pdtsFamilies?: ProductsFamily[];
	isSelected = '';
	isSelectedMQSY = '';
	periodStartYear = 0;
	periodEndYear = 0;
	prdEndMonth = 0;
	prdStartMonth = 0;

	ngOnInit() {
		this.displayedOfferHeader = this.createDisplayedOfferHeader(
			this.header,
			this.bocs
		);
		this.productsFamilies = this.createProductsFamilies(this.offers);
		this.createPeriodSimplificationDisp(this.header);
	}

	private createDisplayedOfferHeader(
		header: OfferHeader,
		bocs: BocClient[]
	) {
		const bocSoldTo = bocs.find(x => x.businessId === header.soldTo);
		const bocShipTo = bocs.find(x => x.businessId === header.shipTo);
		const bocEndUser = bocs.find(x => x.businessId === header.endUser);
		const currencyValue = header.currency
			? CURRENCY_VALUES_SYMBOLE.find(x => x.code === header.currency)
			: undefined;
		return {
			rfqRef: header.rfqRef || UNDERSCORE,
			referenceAm: header.referenceAm || UNDERSCORE,
			soldTo: bocSoldTo ? `${ bocSoldTo.name } / ${ bocSoldTo.city }` : UNDERSCORE,
			shipTo: bocShipTo ? `${ bocShipTo.name } / ${ bocShipTo.city }` : UNDERSCORE,
			endUser: bocEndUser ? `${ bocEndUser.name } / ${ bocEndUser.city }` : UNDERSCORE,
			period: createOfferPeriod(header),
			idd: header.idd || UNDERSCORE,
			tmode: header.tmode || UNDERSCORE,
			incoterm: header.incoterm || UNDERSCORE,
			priceUnit: header.pricingUnitDescription || UNDERSCORE,
			currency: currencyValue
				? currencyValue.value
				: header.currency || UNDERSCORE,
			comment: header.comment,
			lastModificationDate: header.modificationDate
				? moment(header.modificationDate).format(getFormatDate('date'))
				: UNDERSCORE,
			validityDate: header.validityDate
				? moment(header.validityDate).format(getFormatDate('date'))
				: UNDERSCORE,
			manager: header.manager || UNDERSCORE,
			assistant: header.assistant || UNDERSCORE,
			technician: header.technician || UNDERSCORE,
			weightAndInvoicing: header.weightAndInvoicing || UNDERSCORE,
			paymentTerms: header.paymentTerms || UNDERSCORE,
			incotDescription: header.incotDescription || UNDERSCORE
		};
	}

	private createPeriodSimplificationDisp(header: OfferHeader) {
		const { periodStartMonth, periodEndMonth } = header;
		if (periodStartMonth && periodEndMonth) {
			this.prdEndMonth = Number.parseInt(periodEndMonth.slice(-2), 10);
			this.prdStartMonth = Number.parseInt(periodStartMonth.slice(-2), 10);
			this.periodStartYear = Number.parseInt(periodStartMonth.slice(0, 4), 10);
			this.periodEndYear = Number.parseInt(periodEndMonth.slice(0, 4), 10);
			const deltaMonth = this.prdEndMonth - this.prdStartMonth + 1
				+ ((this.periodEndYear - this.periodStartYear) * 12);
			if (deltaMonth === 12 && (this.prdStartMonth === 1 && this.prdEndMonth === 12)) {
				this.isSelected = 'Year';
				this.isSelectedMQSY = '01';
			} else if (deltaMonth === 6 && this.prdStartMonth % 6 === 1) {
				this.isSelected = 'Semester';
				this.isSelectedMQSY = this.prdStartMonth === 1 ? '01' : '02';
			} else if (deltaMonth === 3 && this.prdStartMonth % 3 === 1) {
				this.isSelected = 'Quarter';
				if (this.prdEndMonth < 7) {
					this.isSelectedMQSY = this.prdEndMonth === 3 ? '01' : '02';
				} else if (this.prdEndMonth > 6) {
					this.isSelectedMQSY = this.prdEndMonth === 9 ? '03' : '04';
				}
			} else if (deltaMonth === 1) {
				this.isSelected = 'Month';
				this.isSelectedMQSY = this.prdStartMonth < 10
					? `0${ this.prdStartMonth.toString() }` : this.prdStartMonth.toString();
			} else {
				this.isSelected = 'Otherpcp';
				this.isSelectedMQSY = '';
			}
		}
	}

	private createProductsFamilies(offer: Offer) {
		if (!offer.products || offer.products.length === 0) {
			return [];
		}
		this.pdtsFamilies = [];
		for (const product of offer.products) {
			const { family } = product.description;

			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (family) {
				let productsFamily = this.pdtsFamilies.find(x => x.familyCode === family.code);
				if (productsFamily !== undefined) {
					productsFamily.noOfProducts += 1;
					if (product.quantity && productsFamily.quantity) {
						productsFamily.quantity += product.quantity;
					}
				} else {
					productsFamily = {
						productFamilyCategory: family.subCategory || UNDERSCORE,
						form: family.form || UNDERSCORE,
						familyCode: family.code,
						quantity: product.quantity || 0,
						noOfProducts: 1,
					};
					this.pdtsFamilies.push(productsFamily);
				}
			}
		}
		if (offer.header.offerType === 'framework' && offer.listOfOfferFamilyVolume !== undefined) {
			for (const volumeObj of offer.listOfOfferFamilyVolume) {
				const productsFamily = this.pdtsFamilies.find(x => x.familyCode === volumeObj.familyCode);
				if (productsFamily !== undefined && volumeObj.quantity && productsFamily.quantity !== undefined) {
					productsFamily.quantity += volumeObj.quantity;
				}
			}
		}
		return this.pdtsFamilies;
	}
}
